import { Link, createFileRoute } from '@tanstack/react-router'
import {
  BookOpenText,
  Calculator,
  Gauge,
  Lightbulb,
  Magnet,
  Presentation,
  Ruler,
  type LucideIcon,
} from 'lucide-react'
import { can, flag } from '@/auth/me'
import { Card } from '@/components/ui/card'
import { ToolSwitches } from '@/features/tools/tool-switches'

export const Route = createFileRoute('/_app/tools/')({ component: Tools })

type Tool = { flag: string; title: string; description: string; icon: LucideIcon }

const TOOLS: Tool[] = [
  {
    flag: 'tool_roi',
    title: 'Energy Savings & ROI',
    description: 'Savings, payback and CO2 for a retrofit.',
    icon: Calculator,
  },
  {
    flag: 'tool_lux_estimator',
    title: 'Quick Lux Estimator',
    description: 'Rough fitting count by the lumen method.',
    icon: Gauge,
  },
  {
    flag: 'tool_lux_reference',
    title: 'Lux Level Reference',
    description: 'Recommended lux by space type.',
    icon: BookOpenText,
  },
  {
    flag: 'tool_catalogue',
    title: 'Product Catalogue',
    description: 'Search products and spec sheets offline.',
    icon: Lightbulb,
  },
  {
    flag: 'tool_strip_calculator',
    title: 'Strip Light Calculator',
    description: 'Load, driver size and feeds for a run.',
    icon: Ruler,
  },
  {
    flag: 'tool_magniko',
    title: 'MagNiko Configurator',
    description: 'Configure a MagNiko magnetic track.',
    icon: Magnet,
  },
  {
    flag: 'tool_presentation_mode',
    title: 'Presentation Mode',
    description: 'A clean view to show clients.',
    icon: Presentation,
  },
]

const HREF: Record<string, string> = {
  tool_roi: '/tools/roi',
  tool_lux_estimator: '/tools/lux',
  tool_lux_reference: '/tools/reference',
  tool_catalogue: '/products',
  tool_strip_calculator: '/tools/strip',
  tool_magniko: '/tools/magniko',
  tool_presentation_mode: '/present',
}

function Tools() {
  const { me } = Route.useRouteContext()
  // Each tool sits behind its own feature switch (Admin > Features).
  const tools = TOOLS.filter((t) => flag(me, t.flag))
  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <h1 className="text-3xl uppercase">Tools</h1>
      <ul className="grid gap-4 sm:grid-cols-2">
        {tools.map((tool) => (
          <li key={tool.flag}>
            <Link
              to={HREF[tool.flag]!}
              className="block h-full rounded-card hover:ring-2 hover:ring-brand"
            >
              <Card className="flex h-full items-start gap-4">
                <span className="flex size-12 shrink-0 items-center justify-center rounded-control bg-surface-2">
                  <tool.icon className="size-6" aria-hidden />
                </span>
                <div className="flex flex-col gap-1">
                  <h2 className="text-lg font-semibold uppercase">{tool.title}</h2>
                  <p className="text-fg-muted">{tool.description}</p>
                </div>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
      {tools.length === 0 && <p className="text-fg-muted">No tools are switched on right now.</p>}
      {can(me, 'admin') && !me.impersonating && <ToolSwitches tools={TOOLS} />}
    </div>
  )
}
